import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { X, Cookie, Settings2, Check } from "lucide-react";
import { useT, localizedPath } from "../i18n";

const STORAGE_KEY = "mrx_consent_v1";

function pushConsent(consent) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  gtag("consent", "update", consent);
  window.dataLayer.push({ event: "cookie_consent_update" });
}

function persist(consent) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
  } catch (e) {}
}

const DENIED_ALL = {
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
  analytics_storage: "denied",
  functionality_storage: "granted",
  personalization_storage: "denied",
  security_storage: "granted",
};

const GRANTED_ALL = {
  ad_storage: "granted",
  ad_user_data: "granted",
  ad_personalization: "granted",
  analytics_storage: "granted",
  functionality_storage: "granted",
  personalization_storage: "granted",
  security_storage: "granted",
};

export default function CookieBanner() {
  const { t, lang } = useT();
  const [visible, setVisible] = useState(false);
  const [manage, setManage] = useState(false);
  const [prefs, setPrefs] = useState({
    functional: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    let saved = null;
    try {
      saved = localStorage.getItem(STORAGE_KEY);
    } catch (e) {}
    if (!saved) {
      // small delay so it doesn't clash with hero reveal
      const id = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(id);
    }
  }, []);

  const acceptAll = () => {
    pushConsent(GRANTED_ALL);
    persist(GRANTED_ALL);
    setVisible(false);
    setManage(false);
  };

  const rejectAll = () => {
    pushConsent(DENIED_ALL);
    persist(DENIED_ALL);
    setVisible(false);
    setManage(false);
  };

  const savePrefs = () => {
    const consent = {
      ad_storage: prefs.marketing ? "granted" : "denied",
      ad_user_data: prefs.marketing ? "granted" : "denied",
      ad_personalization: prefs.marketing ? "granted" : "denied",
      analytics_storage: prefs.analytics ? "granted" : "denied",
      functionality_storage: prefs.functional ? "granted" : "denied",
      personalization_storage: prefs.functional ? "granted" : "denied",
      security_storage: "granted",
    };
    pushConsent(consent);
    persist(consent);
    setVisible(false);
    setManage(false);
  };

  if (!visible) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="cookie-banner"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 24 }}
        transition={{ duration: 0.4, ease: [0.65, 0, 0.35, 1] }}
        className="fixed inset-x-0 bottom-0 z-[80] px-4 pb-4 sm:px-6 sm:pb-6"
        data-testid="cookie-banner"
        role="dialog"
        aria-live="polite"
        aria-label={t("cookies.title")}
      >
        <div className="max-w-[1200px] mx-auto glass-dark rounded-2xl overflow-hidden cookie-shell text-[#f6f4ee]">
          {!manage ? (
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6 p-6 sm:p-8 items-center">
              <div className="flex items-start gap-4">
                <div className="hidden sm:flex h-11 w-11 rounded-full bg-[#c5b293]/15 items-center justify-center shrink-0">
                  <Cookie size={20} className="text-[#c5b293]" />
                </div>
                <div>
                  <div className="font-serif text-2xl leading-tight mb-1.5">{t("cookies.title")}</div>
                  <p className="text-[0.9rem] leading-relaxed text-[#f6f4ee]/75 max-w-2xl">
                    {t("cookies.body")}{" "}
                    <Link
                      to={localizedPath(lang, "cookie-policy")}
                      className="link-underline text-[#c5b293]"
                      data-testid="cookie-banner-policy-link"
                    >
                      {t("cookies.learnMore")}
                    </Link>
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2.5 lg:justify-end">
                <button
                  type="button"
                  onClick={() => setManage(true)}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-[0.85rem] border border-[#f6f4ee]/25 hover:bg-[#f6f4ee]/8 transition-colors"
                  data-testid="cookie-manage-btn"
                >
                  <Settings2 size={14} /> {t("cookies.manage")}
                </button>
                <button
                  type="button"
                  onClick={rejectAll}
                  className="inline-flex items-center px-4 py-2.5 rounded-full text-[0.85rem] border border-[#f6f4ee]/25 hover:bg-[#f6f4ee]/8 transition-colors"
                  data-testid="cookie-reject-btn"
                >
                  {t("cookies.rejectAll")}
                </button>
                <button
                  type="button"
                  onClick={acceptAll}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-[0.85rem] bg-[#f6f4ee] text-[#1a211c] font-medium hover:bg-[#c5b293] transition-colors"
                  data-testid="cookie-accept-btn"
                >
                  <Check size={14} /> {t("cookies.acceptAll")}
                </button>
              </div>
            </div>
          ) : (
            <div className="p-6 sm:p-8">
              <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                  <div className="font-serif text-2xl leading-tight mb-1.5">{t("cookies.manage")}</div>
                  <p className="text-[0.88rem] text-[#f6f4ee]/70 max-w-2xl">{t("cookies.body")}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setManage(false)}
                  className="h-9 w-9 rounded-full border border-[#f6f4ee]/25 inline-flex items-center justify-center"
                  aria-label={t("cookies.close")}
                  data-testid="cookie-manage-close"
                >
                  <X size={16} />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <PrefRow
                  title={t("cookies.categories.necessary.title")}
                  desc={t("cookies.categories.necessary.desc")}
                  locked
                  value={true}
                  onChange={() => {}}
                  testid="cookie-pref-necessary"
                  lockedLabel={t("cookies.categories.necessary.always")}
                />
                <PrefRow
                  title={t("cookies.categories.functional.title")}
                  desc={t("cookies.categories.functional.desc")}
                  value={prefs.functional}
                  onChange={(v) => setPrefs((p) => ({ ...p, functional: v }))}
                  testid="cookie-pref-functional"
                />
                <PrefRow
                  title={t("cookies.categories.analytics.title")}
                  desc={t("cookies.categories.analytics.desc")}
                  value={prefs.analytics}
                  onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
                  testid="cookie-pref-analytics"
                />
                <PrefRow
                  title={t("cookies.categories.marketing.title")}
                  desc={t("cookies.categories.marketing.desc")}
                  value={prefs.marketing}
                  onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
                  testid="cookie-pref-marketing"
                />
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-2.5 justify-end">
                <button
                  type="button"
                  onClick={rejectAll}
                  className="inline-flex items-center px-4 py-2.5 rounded-full text-[0.85rem] border border-[#f6f4ee]/25 hover:bg-[#f6f4ee]/8 transition-colors"
                  data-testid="cookie-manage-reject"
                >
                  {t("cookies.rejectAll")}
                </button>
                <button
                  type="button"
                  onClick={savePrefs}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-[0.85rem] bg-[#c5b293] text-[#1a211c] font-medium hover:bg-[#f6f4ee] transition-colors"
                  data-testid="cookie-save-btn"
                >
                  <Check size={14} /> {t("cookies.save")}
                </button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

function PrefRow({ title, desc, value, onChange, locked, lockedLabel, testid }) {
  return (
    <div className="rounded-xl border border-[#f6f4ee]/12 bg-[#f6f4ee]/5 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[0.95rem] font-medium">{title}</div>
          <p className="text-[0.8rem] text-[#f6f4ee]/65 mt-1 leading-relaxed">{desc}</p>
        </div>
        {locked ? (
          <span className="text-[0.7rem] font-mono uppercase tracking-widest text-[#c5b293]">
            {lockedLabel}
          </span>
        ) : (
          <Toggle checked={value} onChange={onChange} testid={testid} />
        )}
      </div>
    </div>
  );
}

function Toggle({ checked, onChange, testid }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative h-6 w-11 rounded-full transition-colors ${
        checked ? "bg-[#c5b293]" : "bg-[#f6f4ee]/20"
      }`}
      data-testid={testid}
    >
      <span
        className={`absolute top-0.5 h-5 w-5 rounded-full bg-[#1a211c] transition-transform ${
          checked ? "translate-x-5" : "translate-x-0.5"
        }`}
      />
    </button>
  );
}
